import { useState, useEffect } from 'react';
import { MapPin, Navigation, Car, Bike, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

interface DistanceSelectorSheetProps {
  isOpen: boolean;
  onClose: () => void;
  currentDistance: number | null;
  onDistanceChange: (distance: number | null) => void;
}

const DISTANCE_PRESETS: Array<{ value: number | null; label: string; description: string; icon: typeof Navigation }> = [
  { value: 0.5, label: '0.5 mi', description: 'Walking', icon: Navigation },
  { value: 1, label: '1 mi', description: 'Nearby', icon: Navigation },
  { value: 3, label: '3 mi', description: 'Short drive', icon: Bike },
  { value: 5, label: '5 mi', description: 'Local', icon: Car },
  { value: 10, label: '10 mi', description: 'Extended', icon: Car },
  { value: 100, label: '100 mi', description: 'Max range', icon: MapPin },
  { value: null, label: 'Any Distance', description: 'No limit', icon: Globe },
];

export default function DistanceSelectorSheet({
  isOpen,
  onClose,
  currentDistance,
  onDistanceChange,
}: DistanceSelectorSheetProps) {
  const [selectedDistance, setSelectedDistance] = useState<number | null>(currentDistance ?? 10);
  const [sliderValue, setSliderValue] = useState<number>(currentDistance ?? 10);

  useEffect(() => {
    if (isOpen) {
      setSelectedDistance(currentDistance);
      setSliderValue(currentDistance ?? 10);
    }
  }, [isOpen, currentDistance]);

  const handlePresetClick = (value: number | null) => {
    setSelectedDistance(value);
    if (value !== null) {
      setSliderValue(value);
    }
  };

  const handleSliderChange = (values: number[]) => {
    setSliderValue(values[0]);
    setSelectedDistance(values[0]);
  };

  const handleApply = () => {
    onDistanceChange(selectedDistance);
    onClose();
  };

  const handleClear = () => {
    onDistanceChange(null);
    onClose();
  };

  const formatDistance = (distance: number | null): string => {
    if (distance === null) {
      return 'Any';
    }
    if (Number.isInteger(distance)) {
      return `${distance} mi`;
    }
    return `${distance.toFixed(1)} mi`;
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <SheetContent side="bottom" className="h-auto max-h-[70svh] rounded-t-2xl">
        <SheetHeader className="text-left pb-4">
          <SheetTitle>Search Radius</SheetTitle>
          <SheetDescription>
            Find restaurants within your preferred distance
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-6 py-4">
          <div className="flex flex-wrap gap-2" data-testid="distance-presets">
            {DISTANCE_PRESETS.map((preset) => {
              const Icon = preset.icon;
              const isSelected = preset.value === null 
                ? selectedDistance === null 
                : selectedDistance !== null && Math.abs(selectedDistance - preset.value) < 0.1;
              
              return (
                <Button
                  key={preset.value ?? 'any'}
                  variant={isSelected ? "default" : "outline"}
                  size="sm"
                  onClick={() => handlePresetClick(preset.value)}
                  className={cn(
                    "flex items-center gap-1.5 rounded-full",
                    isSelected && "bg-primary/10 text-primary border-primary/30"
                  )}
                  data-testid={`preset-${preset.value ?? 'any'}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  <span>{preset.label}</span>
                </Button>
              );
            })}
          </div>

          <div className="space-y-4 px-2">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Fine-tune distance</span>
              <span className="text-lg font-semibold text-primary" data-testid="slider-value">
                {formatDistance(selectedDistance)}
              </span>
            </div>
            
            <Slider
              value={[sliderValue]}
              onValueChange={handleSliderChange}
              min={0.5}
              max={100}
              step={0.5}
              className="w-full"
              data-testid="distance-slider"
            />
            
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>0.5 mi</span>
              <span>100 mi</span>
            </div>
          </div>
        </div>

        <SheetFooter className="flex flex-row gap-3 pt-4">
          <Button
            variant="outline"
            onClick={handleClear}
            className="flex-1"
            data-testid="button-clear-distance"
          >
            Clear
          </Button>
          <Button
            onClick={handleApply}
            className="flex-1 bg-primary text-primary-foreground"
            data-testid="button-apply-distance"
          >
            Apply
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
